/* eslint-disable */
import React, { useState, useCallback, useEffect } from "react";

// ═══════════════════════════════════════════════════════════════════════════════
// Confirm Dialog — CLB Sandstone Design
// Replaces window.confirm() with elegant modal
// Usage: const { confirm, ConfirmDialogComponent } = useConfirmDialog();
//        const ok = await confirm({ title: "Delete?", message: "Are you sure?", destructive: true });
// ═══════════════════════════════════════════════════════════════════════════════

export function useConfirmDialog() {
  const [dialog, setDialog] = useState(null);

  const confirm = useCallback(({ title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", destructive = false }) => {
    return new Promise((resolve) => {
      setDialog({ title, message, confirmLabel, cancelLabel, destructive, resolve, visible: true });
    });
  }, []);

  const handleConfirm = () => {
    if (dialog?.resolve) dialog.resolve(true);
    setDialog(prev => prev ? { ...prev, visible: false } : null);
    setTimeout(() => setDialog(null), 200);
  };

  const handleCancel = () => {
    if (dialog?.resolve) dialog.resolve(false);
    setDialog(prev => prev ? { ...prev, visible: false } : null);
    setTimeout(() => setDialog(null), 200);
  };

  // ESC to cancel
  useEffect(() => {
    if (!dialog) return;
    const handler = (e) => { if (e.key === "Escape") handleCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dialog]);

  const ConfirmDialogComponent = dialog ? (
    <div
      onClick={handleCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 'var(--z-toast)',
        background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        opacity: dialog.visible ? 1 : 0,
        transition: "opacity 0.2s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 400, background: "var(--card-bg)", borderRadius:'var(--radius-lg)',
          padding: "var(--space-3xl) var(--space-2xl)",
          fontFamily: "'Montserrat',system-ui,sans-serif",
          transform: dialog.visible ? "scale(1)" : "scale(0.96)",
          opacity: dialog.visible ? 1 : 0,
          transition: "transform 0.2s ease, opacity 0.2s ease",
        }}
      >
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)', letterSpacing: "1px", textTransform: "uppercase", color: "var(--text-soft)" }}>
          {dialog.title}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: "var(--sandstone)", marginTop: 'var(--space-sm)', lineHeight: 1.6 }}>
          {dialog.message}
        </div>
        <div style={{ display: "flex", gap: 'var(--space-md)', justifyContent: "flex-end", marginTop: 'var(--space-2xl)'}}>
          <button
            onClick={handleCancel}
            style={{
              background: "transparent", border:"var(--border-thin) solid var(--border)",
              color: "var(--sandstone)", padding: "var(--space-sm) var(--space-xl)", borderRadius:'var(--radius-xs)',
              fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-medium)', letterSpacing: "1px", textTransform: "uppercase",
              cursor: "pointer", fontFamily: "'Montserrat',system-ui,sans-serif",
            }}
          >
            {dialog.cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            style={{
              background: dialog.destructive ? "#c4836a" : "var(--sandstone)",
              color: "var(--card-bg)", padding: "var(--space-sm) var(--space-xl)", borderRadius:'var(--radius-xs)', border: "none",
              fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-medium)', letterSpacing: "1px", textTransform: "uppercase",
              cursor: "pointer", fontFamily: "'Montserrat',system-ui,sans-serif",
            }}
          >
            {dialog.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, ConfirmDialogComponent };
}
